import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion as Motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import cls from "./UniverseInfo.module.scss";

import LogoS02 from "@/assets/NEW/logo/S02.webp";
import MrM from "@/assets/UI/uni/MrM.webp";
import MrsM from "@/assets/UI/uni/MrsM.webp";
import MrD from "@/assets/UI/uni/MrD.webp";
import MrsD from "@/assets/UI/uni/MrsD.webp";

const bgFloatA = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.5, 0.68, 0.5],
    scale: [1.03, 1.08, 1.03],
    x: [0, 8, 0],
    transition: { duration: 13, ease: "easeInOut", repeat: Infinity },
  },
};

const bgFloatB = {
  hidden: { opacity: 0.78 },
  show: {
    opacity: [0.78, 0.62, 0.78],
    scale: [1.08, 1.03, 1.08],
    x: [0, -10, 0],
    transition: { duration: 15, ease: "easeInOut", repeat: Infinity },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 26, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

export default function UniverseInfo() {
  const { t } = useTranslation();

  const [mounted, setMounted] = useState(false);
  const [showTop, setShowTop] = useState(true);

  const chapters = useMemo(
    () =>
      Array.from({ length: 18 }, (_, index) => {
        const chapterNumber = index + 1;

        return {
          id: `chapter-${chapterNumber}`,
          title: t(`universeInfo.chapter${chapterNumber}.title`),
          subtitle: t(`universeInfo.chapter${chapterNumber}.subtitle`),
          body: t(`universeInfo.chapter${chapterNumber}.body`, {
            returnObjects: true,
          }),
        };
      }),
    [t],
  );

  useEffect(() => {
    setMounted(true);

    const handleScroll = () => {
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        setShowTop(true);
        return;
      }

      setShowTop(window.scrollY > 220);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    document.documentElement?.scrollTo?.({
      top: 0,
      left: 0,
      behavior: "smooth",
    });

    document.body?.scrollTo?.({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const topButton =
    mounted &&
    createPortal(
      <AnimatePresence>
        {showTop && (
          <Motion.button
            type="button"
            className={cls.toTopBtn}
            onClick={scrollToTop}
            initial={{ opacity: 0, y: 18, scale: 0.84 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.84 }}
            whileHover={{ y: -3, scale: 1.04 }}
            whileTap={{ scale: 0.94 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            aria-label={t("universeInfo.backToTop", {
              defaultValue: "Top",
            })}
          >
            <span>↑</span>
            <strong>
              {t("universeInfo.backToTop", {
                defaultValue: "Top",
              })}
            </strong>
          </Motion.button>
        )}
      </AnimatePresence>,
      document.body,
    );

  return (
    <>
      <main className={cls.page}>
        <section
          className={cls.heroStage}
          aria-label="Noty Universe information"
        >
          <div className={cls.universeFx} aria-hidden="true">
            <span className={cls.orbitOne} />
            <span className={cls.orbitTwo} />
            <span className={cls.energyLine} />
            <span className={cls.particles} />
          </div>

          <div className={cls.mobileOnly} aria-hidden="true">
            <Motion.img
              className={`${cls.heroImg} ${cls.z0}`}
              src={MrM}
              alt=""
              variants={bgFloatA}
              initial="hidden"
              animate="show"
              draggable="false"
            />

            <Motion.img
              className={`${cls.heroImg} ${cls.z1}`}
              src={MrsM}
              alt=""
              variants={bgFloatB}
              initial="hidden"
              animate="show"
              draggable="false"
            />
          </div>

          <div className={cls.desktopOnly} aria-hidden="true">
            <Motion.img
              className={`${cls.heroImg} ${cls.z0}`}
              src={MrD}
              alt=""
              variants={bgFloatA}
              initial="hidden"
              animate="show"
              draggable="false"
            />

            <Motion.img
              className={`${cls.heroImg} ${cls.z1}`}
              src={MrsD}
              alt=""
              variants={bgFloatB}
              initial="hidden"
              animate="show"
              draggable="false"
            />
          </div>

          <div className={cls.darkVeil} aria-hidden="true" />
        </section>

        <div className={cls.contentWrap}>
          <Motion.header
            className={cls.logoStack}
            initial={{ opacity: 0, y: -16, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className={cls.logoPortal}>
              <span className={cls.logoGlow} />
              <span className={cls.logoRing} />
              <img src={LogoS02} alt="Noty Universe" className={cls.logo} />
            </div>

            <div
              className={cls.districtMorph}
              aria-label="Nova Oracle Terra Yara Noty Universe animation"
            >
              <div className={cls.districtNames}>
                <span>
                  <strong>N</strong>
                  <em>ova</em>
                </span>

                <span>
                  <strong>O</strong>
                  <em>racle</em>
                </span>

                <span>
                  <strong>T</strong>
                  <em>erra</em>
                </span>

                <span>
                  <strong>Y</strong>
                  <em>ara</em>
                </span>
              </div>

              <div className={cls.notyWord}>
                <span>NOTY</span>
                <small>Universe</small>
              </div>
            </div>

            <div className={cls.heroTitleBox}>
              <span className={cls.kicker}>Universe Guide</span>
              <h1>{t("universeInfo.title")}</h1>
              <p>{t("universeInfo.intro")}</p>
            </div>
          </Motion.header>

          <Motion.nav
            className={cls.chapterNav}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2, ease: "easeOut" }}
          >
            {chapters.map((chapter, index) => (
              <a key={chapter.id} href={`#${chapter.id}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {chapter.title}
              </a>
            ))}
          </Motion.nav>

          <section className={cls.chapterList}>
            {chapters.map((chapter, index) => (
              <Motion.article
                id={chapter.id}
                key={chapter.id}
                className={cls.chapterCard}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.18 }}
                transition={{ delay: index * 0.08 }}
              >
                <span className={cls.chapterNumber}>
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div className={cls.chapterContent}>
                  <h2>{chapter.title}</h2>
                  <h3>{chapter.subtitle}</h3>

                  {Array.isArray(chapter.body) &&
                    chapter.body.map((line, lineIndex) => (
                      <p key={`${chapter.id}-${lineIndex}`}>{line}</p>
                    ))}
                </div>
              </Motion.article>
            ))}
          </section>
        </div>
      </main>

      {topButton}
    </>
  );
}
